import { authService } from "@/api/auth-service";
import { Button } from "@/components/core/buttons/button";
import { Form, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const resetPasswordSchema = z
	.object({
		password: z.string().min(8, "Password must be at least 8 characters long"),
		password_confirmation: z.string().min(8, "Password confirmation must be at least 8 characters long"),
	})
	.refine((data) => data.password === data.password_confirmation, {
		message: "Passwords do not match",
		path: ["password_confirmation"],
	});

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export const Route = createFileRoute("/reset-password")({
	component: ResetPasswordPage,
	validateSearch: (search: Record<string, unknown>) => ({
		token: (search.token as string) || "",
		email: (search.email as string) || "",
	}),
});

function ResetPasswordPage() {
	const navigate = useNavigate();
	const { token, email } = useSearch({ from: "/reset-password" });
	const [isLoading, setIsLoading] = useState(false);
	const [passwordReset, setPasswordReset] = useState(false);

	const form = useForm<ResetPasswordFormData>({
		resolver: zodResolver(resetPasswordSchema),
		defaultValues: {
			password: "",
			password_confirmation: "",
		},
	});

	// Redirect if no token or email
	useEffect(() => {
		if (!token || !email) {
			toast.error("Invalid password reset link");
			navigate({ to: "/login" });
		}
	}, [token, email, navigate]);

	const handleSubmit = async (values: ResetPasswordFormData) => {
		setIsLoading(true);

		try {
			await authService.resetPassword({
				token,
				email,
				password: values.password,
				password_confirmation: values.password_confirmation,
			});

			setPasswordReset(true);
			toast.success("Password reset successfully!");
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const errorWithResponse = error as { response?: { status?: number; data?: { errors?: Record<string, string[]> } } };
				if (errorWithResponse.response?.status === 422) {
					const serverErrors = errorWithResponse.response.data?.errors || {};
					for (const key of Object.keys(serverErrors)) {
						form.setError(key as keyof ResetPasswordFormData, {
							message: serverErrors[key][0],
						});
					}
				} else {
					toast.error("Failed to reset password. Please try again or request a new reset link.");
				}
			} else {
				toast.error("Failed to reset password. Please try again or request a new reset link.");
			}
		} finally {
			setIsLoading(false);
		}
	};

	if (passwordReset) {
		return (
			<div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
				<div className="max-w-md w-full space-y-8 text-center">
					<div>
						<div className="mx-auto rounded-lg flex items-center justify-center p-6">
							<img className="h-full w-full object-contain" src="/salesmon-logo.png" alt="Salesmon" />
						</div>
						<h1 className="text-center text-3xl font-extrabold">REVLV SALES MONITORING</h1>
					</div>

					<div className="bg-white p-8 rounded-lg shadow">
						<div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
							<svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" role="img" aria-label="Success checkmark">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
							</svg>
						</div>
						<h2 className="text-lg font-medium text-gray-900 mb-4">Password Reset Successful</h2>
						<p className="text-gray-600 mb-6">Your password has been successfully reset. You can now sign in with your new password.</p>
						<Link to="/login">
							<Button className="w-full">Sign In</Button>
						</Link>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
			<div className="max-w-md w-full space-y-8">
				<div>
					<div className="mx-auto rounded-lg flex items-center justify-center p-6">
						<img className="h-full w-full object-contain" src="/salesmon-logo.png" alt="Salesmon" />
					</div>
					<h1 className="text-center text-3xl font-extrabold">REVLV SALES MONITORING</h1>
					<h2 className="mt-6 text-center text-3xl font-extrabold">Reset Your Password</h2>
					<p className="mt-2 text-center text-gray-600">Enter your new password below</p>
				</div>

				<Form {...form}>
					<form id="reset-password-form" className="mt-8 space-y-6" onSubmit={form.handleSubmit(handleSubmit)}>
						<div className="space-y-4">
							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>New Password</FormLabel>
										<FormGroup>
											<Input
												type="password"
												placeholder="Enter new password"
												autoComplete="new-password"
												{...field}
												disabled={isLoading}
												className="mt-1 block w-full"
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="password_confirmation"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Confirm New Password</FormLabel>
										<FormGroup>
											<Input
												type="password"
												placeholder="Confirm new password"
												autoComplete="new-password"
												{...field}
												disabled={isLoading}
												className="mt-1 block w-full"
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<div>
							<Button type="submit" className="group relative w-full flex justify-center" loading={isLoading} disabled={isLoading}>
								Reset Password
							</Button>
						</div>

						<div className="text-center">
							<Link to="/login" className="text-sm text-primary-600 hover:text-primary-400">
								Back to Sign In
							</Link>
						</div>
					</form>
				</Form>
			</div>
		</div>
	);
}
